import React from 'react';
import RSVPForm from './RSVPForm';
import VinylPlayer from './VinylPlayer';
import { Heart, Calendar, MapPin, Clock } from 'lucide-react';

export default function TropicalBeach({ 
  data,
  onSubmitRSVP,
  isSubmitting,
  submitSuccess,
  isPreview = false
}) {
  const {
    couple_names = 'John & Jane',
    event_date = '',
    event_time = '',
    venue_name = '',
    venue_address = '',
    story_text = '',
    timeline = [],
    hero_image_url = '',
    hero_photos = [],
    hero_layout = 'fullwidth',
    gallery_images = [],
    color_theme = {},
    font_style = 'sans',
    background_music_url = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = ''
  } = data || {};

  const bgColor = color_theme.background || '#fefaf3';
  const primaryColor = color_theme.primary || '#1a3c34';
  const secondaryColor = color_theme.secondary || '#5c8a7d';
  const accentColor = color_theme.accent || '#e8a849';

  const fontClass = font_style === 'serif' ? 'font-serif' : 'font-sans';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      });
    } catch { return dateStr; }
  };

  const getMainPhoto = () => {
    if (hero_photos?.length > 0) return hero_photos[0];
    if (hero_image_url) return hero_image_url;
    return null;
  };
  const mainPhoto = getMainPhoto();
  const storyPhotoLeft = gallery_images?.[0] || null;
  const storyPhotoRight = gallery_images?.[1] || null;
  const extraGallery = gallery_images?.slice(2) || [];

  // Leaf SVG for decoration
  const LeafDecor = ({ className = '', flip = false }) => (
    <svg 
      viewBox="0 0 40 60" 
      className={className}
      style={{ transform: flip ? 'scaleX(-1)' : undefined }}
      fill="none"
    >
      <path 
        d="M20 58 C20 58 5 40 5 25 C5 10 20 2 20 2 C20 2 35 10 35 25 C35 40 20 58 20 58Z" 
        stroke={accentColor} 
        strokeWidth="1.5" 
        opacity="0.4"
      />
      <path d="M20 8 L20 52" stroke={accentColor} strokeWidth="0.8" opacity="0.3" />
    </svg>
  );

  return (
    <div className={`min-h-screen ${fontClass}`} style={{ backgroundColor: bgColor }}>

      {/* ===== HERO ===== */}
      {mainPhoto ? (
        <div className="relative">
          <img 
            src={mainPhoto}
            alt="Hero"
            className="w-full aspect-[3/4] object-cover"
          />
          {/* Gradient overlay at bottom */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-24"
            style={{ background: `linear-gradient(transparent, ${bgColor})` }}
          />
        </div>
      ) : (
        /* Decorative header when no photo */
        <div className="relative pt-12 pb-6 text-center overflow-hidden">
          <div className="absolute top-0 left-2 w-8 opacity-30">
            <LeafDecor />
          </div>
          <div className="absolute top-0 right-2 w-8 opacity-30">
            <LeafDecor flip />
          </div>
          <p 
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: accentColor }}
          >
            You're Invited
          </p>
        </div>
      )}

      {/* ===== INVITATION TEXT ===== */}
      <div className="relative text-center px-6 pt-6 pb-2">
        {/* Leaf accents flanking the names */}
        <div className="absolute left-3 top-4 w-6 opacity-20">
          <LeafDecor />
        </div>
        <div className="absolute right-3 top-4 w-6 opacity-20">
          <LeafDecor flip />
        </div>

        <p 
          className="text-[10px] tracking-[0.3em] uppercase mb-3"
          style={{ color: secondaryColor }}
        >
          Together with their families
        </p>
        <h1 
          className="text-3xl font-light tracking-wider leading-tight mb-3"
          style={{ color: primaryColor }}
        >
          {couple_names}
        </h1>
        <p 
          className="text-xs leading-relaxed"
          style={{ color: secondaryColor }}
        >
          Invite you to celebrate their wedding
        </p>
      </div>

      {/* ===== DIVIDER ===== */}
      <div className="flex items-center justify-center gap-3 py-5 px-8">
        <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}30` }} />
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke={accentColor} strokeWidth="1.2" opacity="0.6">
          <path d="M12 3C7 3 3 7.5 3 12s4.5 9 9 9c1.5-3 3-6 3-9S13.5 3 12 3z" />
          <path d="M12 3c5 0 9 4.5 9 9s-4.5 9-9 9" />
          <path d="M12 7v10M8 12h8" />
        </svg>
        <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}30` }} />
      </div>

      {/* ===== DATE + VENUE ===== */}
      <div className="text-center px-6 pb-6 space-y-3">
        {event_date && (
          <div className="flex items-center justify-center gap-2">
            <Calendar className="w-3.5 h-3.5" style={{ color: accentColor }} />
            <p className="text-sm tracking-wide" style={{ color: primaryColor }}>
              {formatDate(event_date)}
            </p>
          </div>
        )}
        {event_time && (
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-3.5 h-3.5" style={{ color: accentColor }} />
            <p className="text-sm" style={{ color: secondaryColor }}>{event_time}</p>
          </div>
        )}
        {(venue_name || venue_address) && (
          <div className="flex items-center justify-center gap-2">
            <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: accentColor }} />
            <div>
              {venue_name && <p className="text-sm" style={{ color: primaryColor }}>{venue_name}</p>}
              {venue_address && <p className="text-xs" style={{ color: secondaryColor }}>{venue_address}</p>}
            </div>
          </div>
        )}
      </div>

      {/* ===== STORY + PHOTOS ===== */}
      {(story_text || storyPhotoLeft || storyPhotoRight) && (
        <div 
          className="mx-4 rounded-2xl px-5 py-8 my-4"
          style={{ backgroundColor: `${accentColor}10` }}
        >
          <p 
            className="text-[10px] tracking-[0.3em] uppercase text-center mb-5"
            style={{ color: accentColor }}
          >
            Our Story
          </p>

          {(storyPhotoLeft || storyPhotoRight) && (
            <div className="space-y-4 mb-6">
              {/* Row 1: Small photo left + text right */}
              {storyPhotoLeft && (
                <div className="flex gap-3 items-start">
                  <div className="w-[40%] shrink-0 rounded-xl overflow-hidden">
                    <img src={storyPhotoLeft} alt="Moment" className="w-full aspect-[3/4] object-cover" />
                  </div>
                  {story_text && (
                    <div className="flex-1 flex items-center">
                      <p className="text-xs leading-relaxed" style={{ color: secondaryColor }}>
                        {story_text}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Row 2: Text left + big photo right */}
              {storyPhotoRight && (
                <div className="flex gap-3 items-start">
                  <div className="w-[35%] shrink-0 flex items-end pt-4">
                    <p className="text-xs italic" style={{ color: accentColor }}>
                      {couple_names}
                    </p>
                  </div>
                  <div className="flex-1 rounded-xl overflow-hidden">
                    <img src={storyPhotoRight} alt="Celebration" className="w-full aspect-[3/4] object-cover" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Story text when no story photos */}
          {story_text && !storyPhotoLeft && (
            <p 
              className="text-sm leading-relaxed text-center max-w-sm mx-auto"
              style={{ color: secondaryColor }}
            >
              {story_text}
            </p>
          )}
        </div>
      )}

      {/* ===== TIMELINE ===== */}
      {timeline?.length > 0 && (
        <div className="px-6 py-8">
          <p 
            className="text-[10px] tracking-[0.3em] uppercase text-center mb-6"
            style={{ color: accentColor }}
          >
            Day Schedule
          </p>
          <div className="space-y-4 max-w-xs mx-auto">
            {timeline.map((item, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="flex flex-col items-center">
                  <div 
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: accentColor, opacity: 0.6 }}
                  />
                  {idx < timeline.length - 1 && (
                    <div className="w-px h-6" style={{ backgroundColor: `${accentColor}25` }} />
                  )}
                </div>
                <div className="flex-1 flex items-baseline gap-3">
                  <span className="text-xs shrink-0" style={{ color: secondaryColor }}>{item.time}</span>
                  <span className="text-sm" style={{ color: primaryColor }}>{item.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== GALLERY ===== */}
      {extraGallery.length > 0 && (
        <div className="px-4 py-6">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {extraGallery.slice(0, 6).map((img, idx) => (
              <div key={idx} className="flex-shrink-0 rounded-xl overflow-hidden snap-center" style={{ width: '110px' }}>
                <img src={img} alt={`Gallery ${idx + 1}`} className="w-full aspect-[3/4] object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== VINYL PLAYER ===== */}
      {background_music_url && (
        <VinylPlayer
          audioSrc={background_music_url}
          coverImage={vinyl_cover_image || getMainPhoto()}
          songTitle={song_title}
          artistName={artist_name}
          accentColor={accentColor}
          bgColor={bgColor}
          isPreview={isPreview}
        />
      )}

      {/* ===== RSVP ===== */}
      <div 
        className="mx-4 rounded-2xl px-5 py-8 my-4"
        style={{ backgroundColor: `${primaryColor}08` }}
      >
        <p 
          className="text-[10px] tracking-[0.3em] uppercase text-center mb-6"
          style={{ color: accentColor }}
        >
          Kindly Respond
        </p>
        {!isPreview ? (
          <RSVPForm
            onSubmit={onSubmitRSVP}
            colorTheme={color_theme}
            fontStyle={font_style}
            isSubmitting={isSubmitting}
            submitSuccess={submitSuccess}
          />
        ) : (
          <div 
            className="text-center py-6 border rounded-xl"
            style={{ borderColor: `${accentColor}25` }}
          >
            <p className="text-xs" style={{ color: secondaryColor }}>RSVP form will appear here</p>
          </div>
        )}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="relative text-center px-6 py-10 overflow-hidden">
        <div className="absolute bottom-0 left-4 w-8 opacity-15">
          <LeafDecor />
        </div>
        <div className="absolute bottom-0 right-4 w-8 opacity-15">
          <LeafDecor flip />
        </div>
        <p className="text-xs tracking-wider relative z-10" style={{ color: secondaryColor }}>
          Can't wait to celebrate with you
        </p>
        <div className="flex items-center justify-center gap-2 mt-3 relative z-10">
          <div className="w-6 h-px" style={{ backgroundColor: `${accentColor}40` }} />
          <Heart className="w-3 h-3" style={{ color: accentColor }} fill={accentColor} />
          <div className="w-6 h-px" style={{ backgroundColor: `${accentColor}40` }} />
        </div>
      </div>
    </div>
  );
}
